import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from '../../icons/Icon';
import { domainIcons } from '../../icons/registry';
import { Tooltip } from '../../primitives/Tooltip/Tooltip';
import { useDsT } from '../../services/i18n';

export interface RoutePoint {
  /** City / port / facility name. */
  name: string;
  /** ISO 3166-1 alpha-2, shown after the name (`Shanghai, CN`). */
  countryCode?: string;
  /** UN/LOCODE or facility code, shown in mono when `showCodes` is on. */
  code?: string;
}

export type RouteDisplayVariant = 'full' | 'compact';

export interface RouteDisplayProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  origin: RoutePoint;
  destination: RoutePoint;
  /** Intermediate stops in travel order. */
  via?: RoutePoint[];
  /** `full` lists every via point; `compact` collapses them to a "+N via" chip (default `full`). */
  variant?: RouteDisplayVariant;
  /** Leading pin glyph (default on). */
  showPin?: boolean;
  /** W7: points wrap onto a second line instead of truncating (cards; grid cells keep one line). */
  wrap?: boolean;
  /** Show each point's code in mono after its name. */
  showCodes?: boolean;
  /** Text for the collapsed via chip; `{count}` is replaced. Default `+{count} via`. */
  viaCountLabel?: string;
  /** Accessible name of the arrow between points. Default `to`. */
  toLabel?: string;
  /** Accessible prefix for the via list. Default `via`. */
  viaLabel?: string;
}

/** `Shanghai, CN` — the point as the product writes it everywhere. */
export function formatRoutePoint(point: RoutePoint): string {
  return point.countryCode ? `${point.name}, ${point.countryCode.toUpperCase()}` : point.name;
}

/** `Shanghai, CN → Singapore, SG → Los Angeles, US` (the arrow is a real character; for `title`s and tooltips). */
export function formatRoute(origin: RoutePoint, destination: RoutePoint, via: RoutePoint[] = []): string {
  return [origin, ...via, destination].map(formatRoutePoint).join(' → ');
}

function Point({ point, showCodes, muted, wrap }: { point: RoutePoint; showCodes?: boolean; muted?: boolean; wrap?: boolean }) {
  return (
    <span className={cn('inline-flex min-w-0 items-baseline gap-1', muted && 'text-fg-muted')}>
      <span className={wrap ? 'break-words' : 'truncate'}>{formatRoutePoint(point)}</span>
      {showCodes && point.code ? <span className="shrink-0 type-mono type-caption text-fg-subtle">{point.code}</span> : null}
    </span>
  );
}

function Arrow({ label }: { label: string }) {
  return (
    <span className="inline-flex shrink-0 items-center text-fg-subtle">
      <Icon name="arrow-right" size={16} aria-hidden="true" />
      <span className="sr-only"> {label} </span>
    </span>
  );
}

/**
 * Origin → destination with optional via points. Logical properties and a mirrored arrow keep it correct in
 * RTL; the full route is offered in a tooltip because grid cells truncate it.
 */
export const RouteDisplay = forwardRef<HTMLSpanElement, RouteDisplayProps>(function RouteDisplay(
  { origin, destination, via = [], variant = 'full', showPin = true, showCodes, wrap, viaCountLabel: viaCountLabelProp, toLabel: toLabelProp, viaLabel: viaLabelProp, className, ...rest },
  ref,
) {
  const t = useDsT();
  const viaCountLabel = viaCountLabelProp ?? t('ds.routeDisplay.viaCount', '+{count} via');
  const toLabel = toLabelProp ?? t('ds.routeDisplay.to', 'to');
  const viaLabel = viaLabelProp ?? t('ds.routeDisplay.via', 'via');
  const full = formatRoute(origin, destination, via);
  const collapse = variant === 'compact' && via.length > 0;
  const middle = collapse ? [] : via;
  return (
    <Tooltip content={full}>
      <span
        ref={ref}
        data-variant={variant}
        className={cn('zo-route inline-flex max-w-full min-w-0 items-center gap-1.5 type-body text-fg', className)}
        {...rest}
      >
        {showPin ? <Icon name={domainIcons.location} size={16} aria-hidden="true" className="shrink-0 text-fg-muted" /> : null}
        <span className={cn('min-w-0 items-center gap-1', wrap ? 'flex flex-wrap gap-y-0.5' : 'inline-flex truncate')}>
          <Point point={origin} showCodes={showCodes} wrap={wrap} />
          {middle.map((point, i) => (
            <span key={`${point.name}-${i}`} className="inline-flex min-w-0 items-center gap-1">
              <Arrow label={toLabel} />
              <Point point={point} showCodes={showCodes} muted wrap={wrap} />
            </span>
          ))}
          <Arrow label={toLabel} />
          <Point point={destination} showCodes={showCodes} wrap={wrap} />
        </span>
        {collapse ? (
          <span
            className="inline-flex h-5 shrink-0 items-center rounded-sm bg-neutral-subtle px-1.5 type-caption text-neutral-fg tnum"
            aria-label={`${viaLabel} ${via.map(formatRoutePoint).join(', ')}`}
            data-via-count={via.length}
          >
            {viaCountLabel.replace('{count}', String(via.length))}
          </span>
        ) : null}
      </span>
    </Tooltip>
  );
});
